import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../state/auth.js';
import { useT } from '../state/i18n.js';

/** 우상단 프로필 드롭다운 — 아바타/이메일 트리거 + 계정·워크스페이스·요금제·로그아웃. */
export function ProfileMenu(): JSX.Element | null {
  const auth = useAuth();
  const t = useT();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const user = auth.user;
  if (!user) return null;
  const initials = (user.name?.trim()?.[0] ?? user.email[0] ?? '?').toUpperCase();
  const close = (): void => setOpen(false);

  return (
    <div className="profile-menu" ref={ref}>
      <button
        type="button"
        className="profile-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t({ ko: '계정 메뉴', en: 'Account menu' })}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="profile-avatar" aria-hidden="true">
          {initials}
        </span>
        <span className="profile-email">{user.email}</span>
        <svg className="profile-caret" width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
          <path d="M2.5 4.5L6 8l3.5-3.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open ? (
        <div className="profile-dropdown" role="menu">
          <div className="profile-head">
            <span className="profile-name">{user.name || user.email.split('@')[0]}</span>
            <span className="profile-sub">{user.email}</span>
          </div>
          <Link role="menuitem" to="/account" className="profile-item" onClick={close}>
            {t({ ko: '계정 설정', en: 'Account settings' })}
          </Link>
          <Link role="menuitem" to="/workspace" className="profile-item" onClick={close}>
            {t({ ko: '워크스페이스', en: 'Workspaces' })}
          </Link>
          <Link role="menuitem" to="/plans" className="profile-item" onClick={close}>
            {t({ ko: '요금제', en: 'Plans' })}
          </Link>
          <div className="profile-divider" role="separator" />
          <button
            type="button"
            role="menuitem"
            className="profile-item profile-logout"
            onClick={() => {
              setOpen(false);
              void auth.logout();
            }}
          >
            {t({ ko: '로그아웃', en: 'Log out' })}
          </button>
        </div>
      ) : null}
    </div>
  );
}
